import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { useI18n } from "../i18n-context";
import { LOGO_SQUARE } from "../assets";

const ROUTES = [
  { id: "home", path: "/" },
  { id: "about", path: "/about" },
  { id: "services", path: "/services" },
  { id: "apps", path: "/apps" },
  { id: "partners", path: "/partners" },
  { id: "faq", path: "/faq" },
  { id: "contact", path: "/contact" },
];

export default function Navbar() {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-brand-ink/5" : "bg-white/40 backdrop-blur-sm"
      }`}
    >
      <div className="container flex items-center justify-between py-4">
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <img src={LOGO_SQUARE} alt="Akwaan" className="h-10 w-10 rounded-lg object-cover" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-display font-extrabold text-brand-ink text-lg tracking-tight">
              Akwaan
            </span>
            <span className="text-[11px] font-mono uppercase text-brand-blue tracking-[0.25em]">
              Express
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {ROUTES.map((r) => (
            <NavLink
              key={r.id}
              to={r.path}
              end={r.path === "/"}
              data-testid={`nav-link-${r.id}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  isActive
                    ? "bg-brand-ink text-white"
                    : "text-brand-ink/70 hover:text-brand-ink hover:bg-brand-ink/5"
                }`
              }
            >
              {t.nav[r.id]}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "ar" ? "en" : "ar")}
            className="px-3 py-2 text-xs font-mono font-bold uppercase tracking-wider text-brand-ink hover:text-brand-blue border border-brand-ink/15 rounded-full transition-all hover:border-brand-blue"
          >
            {lang === "ar" ? "EN" : "ع"}
          </button>
          <Link to="/contact" className="hidden md:inline-flex btn-primary !py-2.5" data-testid="nav-cta">
            {t.nav.cta}
            <ArrowRight className="h-4 w-4 flip-x" />
          </Link>
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-brand-ink/5"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-brand-ink/5">
          <div className="container py-4 flex flex-col gap-1">
            {ROUTES.map((r) => (
              <NavLink
                key={r.id}
                to={r.path}
                end={r.path === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? "bg-brand-ink text-white"
                      : "text-brand-ink hover:bg-brand-ink/5"
                  }`
                }
                data-testid={`mobile-nav-link-${r.id}`}
              >
                {t.nav[r.id]}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
